import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { libraryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    ArrowLeft,
    BookOpen,
    Lightbulb,
    Activity,
    GraduationCap,
    ChevronRight,
    ChevronDown,
    ExternalLink,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Star,
    Brain
} from 'lucide-react';
import Layout from '../components/Layout';

// ── Mnemonic Pill ──────────────────────────────────────────────────────────────
function MnemonicPill({ mnemonic, isDark }) {
    if (!mnemonic) return null;
    return (
        <div className={cn(
            "flex items-start gap-2 mt-2 px-3 py-2 rounded-lg text-xs font-medium",
            isDark
                ? "bg-violet-900/40 border border-violet-700/60 text-violet-200"
                : "bg-violet-50 border border-violet-200 text-violet-800"
        )} data-testid="mnemonic-pill">
            <Brain className={cn(
                "h-3.5 w-3.5 mt-0.5 flex-shrink-0",
                isDark ? "text-violet-400" : "text-violet-500"
            )} />
            <span><span className={cn(
                "font-bold mr-1",
                isDark ? "text-violet-300" : "text-violet-700"
            )}>Mnemonic:</span>{mnemonic}</span>
        </div>
    );
}

// ── Collapsible Section ────────────────────────────────────────────────────────
function CollapsibleSection({ title, children, isDark, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={cn(
            "rounded-xl border overflow-hidden",
            isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"
        )}>
            <button
                className={cn(
                    "w-full flex items-center justify-between px-5 py-4 text-left transition-colors",
                    isDark
                        ? "hover:bg-slate-700/50 text-white"
                        : "hover:bg-slate-50 text-slate-900"
                )}
                onClick={() => setOpen(o => !o)}
            >
                <span className="font-semibold text-base" style={{ fontFamily: 'Manrope' }}>{title}</span>
                {open
                    ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    : <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-400" />
                }
            </button>
            {open && (
                <div className={cn(
                    "px-5 pb-5 pt-1",
                    isDark ? "border-t border-slate-700" : "border-t border-slate-100"
                )}>
                    {children}
                </div>
            )}
        </div>
    );
}

// ── Markdown Table ─────────────────────────────────────────────────────────────
function ContentTable({ headers, rows, isDark }) {
    if (!headers?.length || !rows?.length) return null;
    return (
        <div className="overflow-x-auto rounded-lg mt-3">
            <table className={cn(
                "w-full text-sm border-collapse",
                isDark ? "text-slate-200" : "text-slate-700"
            )}>
                <thead>
                    <tr className={cn(isDark ? "bg-slate-700" : "bg-slate-100")}>
                        {headers.map((h, i) => (
                            <th key={i} className={cn(
                                "px-4 py-2.5 text-left font-semibold whitespace-nowrap",
                                isDark ? "text-white" : "text-slate-800"
                            )}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, ri) => (
                        <tr key={ri} className={cn(
                            "border-t",
                            isDark
                                ? "border-slate-700 hover:bg-slate-700/40"
                                : "border-slate-200 hover:bg-slate-50"
                        )}>
                            {row.map((cell, ci) => (
                                <td key={ci} className="px-4 py-2.5 align-top">{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Book Reference Citation Badge ──────────────────────────────────────────────
function BookRefBadge({ bookRef, isDark }) {
    if (!bookRef) return null;
    return (
        <span className={cn(
            "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded",
            isDark ? "bg-sky-800/60 text-sky-200" : "bg-sky-200/80 text-sky-800"
        )} data-testid="book-ref-badge">
            <BookOpen className="h-2.5 w-2.5" />
            {bookRef.book}{bookRef.page ? `, p.${bookRef.page}` : ''}
        </span>
    );
}

// ── Content Block Renderer ─────────────────────────────────────────────────────
function ContentBlock({ block, isDark }) {
    if (!block) return null;
    const hasBookRef = !!block.book_ref;

    if (block.type === 'text') {
        if (block.high_yield) {
            return (
                <div className={cn(
                    "p-3 rounded-lg my-2",
                    hasBookRef
                        ? (isDark ? "bg-sky-900/30 border-l-4 border-sky-400" : "bg-sky-50 border-l-4 border-sky-400")
                        : (isDark ? "bg-amber-900/30 border-l-4 border-amber-500" : "bg-amber-50 border-l-4 border-amber-400")
                )}>
                    <div className="flex gap-3">
                        <Star className={cn(
                            "h-4 w-4 mt-0.5 flex-shrink-0",
                            hasBookRef
                                ? (isDark ? "text-sky-400" : "text-sky-500")
                                : (isDark ? "text-amber-400" : "text-amber-500")
                        )} />
                        <div className="flex-1">
                            <p className={cn(
                                "text-sm leading-relaxed",
                                hasBookRef
                                    ? (isDark ? "text-sky-100" : "text-sky-900")
                                    : (isDark ? "text-amber-100" : "text-amber-900")
                            )}>
                                {block.text}
                            </p>
                            {hasBookRef && (
                                <div className="mt-1.5">
                                    <BookRefBadge bookRef={block.book_ref} isDark={isDark} />
                                </div>
                            )}
                        </div>
                    </div>
                    {block.mnemonic && <MnemonicPill mnemonic={block.mnemonic} isDark={isDark} />}
                </div>
            );
        }
        if (hasBookRef) {
            return (
                <div className={cn(
                    "p-3 rounded-lg my-2 border-l-4",
                    isDark ? "bg-sky-900/20 border-sky-500" : "bg-sky-50/80 border-sky-300"
                )}>
                    <p className={cn(
                        "leading-relaxed text-sm",
                        isDark ? "text-sky-100" : "text-sky-900"
                    )}>
                        {block.text}
                    </p>
                    <div className="mt-1.5">
                        <BookRefBadge bookRef={block.book_ref} isDark={isDark} />
                    </div>
                </div>
            );
        }
        return (
            <p className={cn(
                "leading-relaxed my-2 text-sm",
                isDark ? "text-slate-300" : "text-slate-700"
            )}>
                {block.text}
            </p>
        );
    }

    if (block.type === 'list') {
        const listBookRef = hasBookRef;
        return (
            <div className={cn(
                "my-3",
                listBookRef
                    ? (isDark ? "bg-sky-900/20 border-l-4 border-sky-400 pl-3 py-2 rounded-r-lg" : "bg-sky-50 border-l-4 border-sky-300 pl-3 py-2 rounded-r-lg")
                    : block.high_yield && (isDark
                        ? "bg-amber-900/20 border-l-4 border-amber-500 pl-3 py-2 rounded-r-lg"
                        : "bg-amber-50 border-l-4 border-amber-400 pl-3 py-2 rounded-r-lg")
            )}>
                {block.high_yield && !listBookRef && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <Star className={cn("h-3 w-3", isDark ? "text-amber-400" : "text-amber-500")} />
                        <span className={cn("text-xs font-semibold uppercase tracking-wide", isDark ? "text-amber-400" : "text-amber-600")}>High-Yield</span>
                    </div>
                )}
                {listBookRef && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <BookOpen className={cn("h-3 w-3", isDark ? "text-sky-400" : "text-sky-500")} />
                        <span className={cn("text-xs font-semibold uppercase tracking-wide", isDark ? "text-sky-400" : "text-sky-600")}>
                            {block.high_yield ? 'High-Yield · From Reference' : 'From Reference'}
                        </span>
                    </div>
                )}
                {block.title && (
                    <p className={cn(
                        "font-semibold text-sm mb-1.5",
                        listBookRef
                            ? (isDark ? "text-sky-200" : "text-sky-800")
                            : block.high_yield
                                ? isDark ? "text-amber-200" : "text-amber-800"
                                : isDark ? "text-slate-200" : "text-slate-800"
                    )}>
                        {block.title}
                    </p>
                )}
                <ul className="space-y-1.5">
                    {(block.items || []).map((item, i) => (
                        <li key={i} className={cn(
                            "flex items-start gap-2 text-sm",
                            listBookRef
                                ? (isDark ? "text-sky-100" : "text-sky-900")
                                : block.high_yield
                                    ? isDark ? "text-amber-100" : "text-amber-900"
                                    : isDark ? "text-slate-300" : "text-slate-700"
                        )}>
                            <span className={cn(
                                "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                                listBookRef ? "bg-sky-500" : block.high_yield ? "bg-amber-500" : "bg-indigo-400"
                            )} />
                            {item}
                        </li>
                    ))}
                </ul>
                {block.high_yield && block.mnemonic && (
                    <MnemonicPill mnemonic={block.mnemonic} isDark={isDark} />
                )}
                {listBookRef && (
                    <div className="mt-2">
                        <BookRefBadge bookRef={block.book_ref} isDark={isDark} />
                    </div>
                )}
            </div>
        );
    }

    if (block.type === 'table') {
        return (
            <div className={cn(
                "my-3",
                hasBookRef && (isDark ? "bg-sky-900/15 border border-sky-700/40 rounded-lg p-3" : "bg-sky-50/60 border border-sky-200 rounded-lg p-3")
            )}>
                {block.title && (
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <p className={cn(
                            "font-semibold text-sm",
                            hasBookRef
                                ? (isDark ? "text-sky-200" : "text-sky-800")
                                : isDark ? "text-slate-200" : "text-slate-800"
                        )}>
                            {block.title}
                        </p>
                        {block.high_yield && (
                            <Badge className={cn(
                                "text-xs py-0",
                                isDark ? "bg-amber-700/60 text-amber-200" : "bg-amber-100 text-amber-700"
                            )}>
                                <Star className="h-2.5 w-2.5 mr-1" />
                                High-Yield
                            </Badge>
                        )}
                        {hasBookRef && <BookRefBadge bookRef={block.book_ref} isDark={isDark} />}
                    </div>
                )}
                <ContentTable headers={block.headers} rows={block.rows} isDark={isDark} />
                {block.high_yield && block.mnemonic && (
                    <MnemonicPill mnemonic={block.mnemonic} isDark={isDark} />
                )}
                {hasBookRef && !block.title && (
                    <div className="mt-2">
                        <BookRefBadge bookRef={block.book_ref} isDark={isDark} />
                    </div>
                )}
            </div>
        );
    }

    return null;
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function LibraryContent() {
    const { category, systemOrSubject, topic } = useParams();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedQuestions, setRelatedQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const decodedSystemOrSubject = decodeURIComponent(systemOrSubject);
    const decodedTopic = decodeURIComponent(topic);

    useEffect(() => {
        loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, systemOrSubject, topic]);

    const loadContent = async () => {
        setLoading(true);
        try {
            const response = await libraryAPI.getContent(category, decodedTopic);
            setContent(response.data);
            loadRelatedQuestions(decodedTopic);
        } catch (err) {
            console.error('Failed to load content:', err);
            toast.error('Failed to load content. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadRelatedQuestions = async (topicName) => {
        setLoadingQuestions(true);
        try {
            const response = await libraryAPI.getRelatedQuestions(topicName);
            setRelatedQuestions(response.data.questions || []);
        } catch (err) {
            console.error('Failed to load related questions:', err);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // ── Loading ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto space-y-4">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-10 w-3/4" />
                    <Skeleton className="h-28 w-full" />
                    <Skeleton className="h-48 w-full" />
                    <Skeleton className="h-36 w-full" />
                </div>
            </Layout>
        );
    }

    // ── Not Found ──────────────────────────────────────────────────────────────
    if (!content) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto text-center py-16">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
                    <h2 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-slate-900")}>
                        Content Not Found
                    </h2>
                    <p className={cn("mb-6", isDark ? "text-slate-300" : "text-slate-600")}>
                        The requested topic could not be loaded. This may be a temporary issue.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                        <Button variant="outline" onClick={() => navigate('/library')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Library
                        </Button>
                        <Button onClick={loadContent} data-testid="retry-content-btn">
                            Retry
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    const CategoryIcon = category === 'systems' ? Activity : GraduationCap;

    // Support both old schema (overview, key_concepts, …) and new schema (sections, key_exam_facts)
    const hasNewFormat = Array.isArray(content.sections) && content.sections.length > 0;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-5 animate-fade-in" data-testid="library-content-page">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm">
                    <Link
                        to="/library"
                        className={cn("hover:underline", isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")}
                    >
                        Library
                    </Link>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                    <span className={cn(isDark ? "text-slate-400" : "text-slate-500")}>{decodedSystemOrSubject}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                    <span className={cn(isDark ? "text-white" : "text-slate-900")}>{content.title || decodedTopic}</span>
                </div>

                {/* Page Header */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <Badge className={cn(
                                category === 'systems'
                                    ? "bg-indigo-100 text-indigo-700"
                                    : "bg-emerald-100 text-emerald-700"
                            )}>
                                <CategoryIcon className="h-3 w-3 mr-1" />
                                {decodedSystemOrSubject}
                            </Badge>
                        </div>
                        <h1 className={cn(
                            "text-3xl md:text-4xl font-bold",
                            isDark ? "text-white" : "text-slate-900"
                        )} style={{ fontFamily: 'Manrope' }}>
                            {content.title || decodedTopic}
                        </h1>
                        {(content.summary || content.overview) && (
                            <p className={cn(
                                "mt-3 text-base leading-relaxed",
                                isDark ? "text-slate-300" : "text-slate-600"
                            )}>
                                {content.summary || content.overview}
                            </p>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/library')}
                        className={cn("flex-shrink-0", isDark && "border-slate-600 text-slate-200")}
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                </div>

                {/* ── BOOK REFERENCES ────────────────────────────────────────── */}
                {content.book_references && content.book_references.length > 0 && (
                    <div className={cn(
                        "rounded-xl border p-4",
                        isDark ? "bg-cyan-900/20 border-cyan-700" : "bg-cyan-50 border-cyan-200"
                    )} data-testid="book-references">
                        <div className="flex items-center gap-2 mb-2">
                            <BookOpen className={cn("h-4 w-4", isDark ? "text-cyan-400" : "text-cyan-600")} />
                            <h3 className={cn("font-semibold text-xs uppercase tracking-wide", isDark ? "text-cyan-300" : "text-cyan-700")}>
                                Cross-Referenced From
                            </h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {content.book_references.map((ref, idx) => (
                                <span key={idx} className={cn(
                                    "text-xs px-2 py-1 rounded-md",
                                    isDark ? "bg-cyan-900/40 text-cyan-200" : "bg-cyan-100 text-cyan-700"
                                )}>
                                    {ref.book}{ref.page ? `, p.${ref.page}` : ''}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── KEY EXAM FACTS (high-yield callout) ───────────────────────── */}
                {content.key_exam_facts && content.key_exam_facts.length > 0 && (
                    <div className={cn(
                        "rounded-xl border-2 p-5",
                        isDark
                            ? "bg-amber-900/20 border-amber-600"
                            : "bg-amber-50 border-amber-300"
                    )} data-testid="key-exam-facts">
                        <div className="flex items-center gap-2 mb-4">
                            <Lightbulb className={cn("h-5 w-5", isDark ? "text-amber-400" : "text-amber-600")} />
                            <h2 className={cn(
                                "font-bold text-base",
                                isDark ? "text-amber-300" : "text-amber-800"
                            )} style={{ fontFamily: 'Manrope' }}>
                                Key Exam Facts
                            </h2>
                            <Badge className={cn(
                                "ml-auto text-xs",
                                isDark ? "bg-amber-700 text-amber-100" : "bg-amber-200 text-amber-800"
                            )}>
                                High-Yield
                            </Badge>
                        </div>
                        <ul className="space-y-4">
                            {content.key_exam_facts.map((item, i) => {
                                // Support both new {fact, mnemonic} objects and old plain strings
                                const factText = typeof item === 'object' ? item.fact : item;
                                const mnemonic = typeof item === 'object' ? item.mnemonic : null;
                                const bookRef = typeof item === 'object' ? item.book_ref : null;
                                return (
                                    <li key={i} className={cn(
                                        "rounded-lg p-3",
                                        bookRef
                                            ? (isDark ? "bg-sky-900/30 border border-sky-700/40" : "bg-sky-50 border border-sky-200")
                                            : (isDark ? "bg-amber-900/30" : "bg-amber-100/60")
                                    )}>
                                        <div className={cn(
                                            "flex items-start gap-3 text-sm",
                                            bookRef
                                                ? (isDark ? "text-sky-100" : "text-sky-900")
                                                : (isDark ? "text-amber-100" : "text-amber-900")
                                        )}>
                                            <CheckCircle2 className={cn(
                                                "h-4 w-4 mt-0.5 flex-shrink-0",
                                                bookRef
                                                    ? (isDark ? "text-sky-400" : "text-sky-500")
                                                    : (isDark ? "text-amber-400" : "text-amber-600")
                                            )} />
                                            <span className="leading-relaxed">{factText}</span>
                                        </div>
                                        {mnemonic && (
                                            <div className="mt-2 ml-7">
                                                <MnemonicPill mnemonic={mnemonic} isDark={isDark} />
                                            </div>
                                        )}
                                        {bookRef && (
                                            <div className="mt-2 ml-7">
                                                <BookRefBadge bookRef={bookRef} isDark={isDark} />
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* ── OLD FORMAT FALLBACK: high_yield_facts ─────────────────────── */}
                {!hasNewFormat && content.high_yield_facts && content.high_yield_facts.length > 0 && (
                    <div className={cn(
                        "rounded-xl border-2 p-5",
                        isDark ? "bg-amber-900/20 border-amber-600" : "bg-amber-50 border-amber-300"
                    )}>
                        <div className="flex items-center gap-2 mb-4">
                            <Lightbulb className={cn("h-5 w-5", isDark ? "text-amber-400" : "text-amber-600")} />
                            <h2 className={cn("font-bold text-base", isDark ? "text-amber-300" : "text-amber-800")} style={{ fontFamily: 'Manrope' }}>
                                High-Yield Facts
                            </h2>
                        </div>
                        <ul className="space-y-2.5">
                            {content.high_yield_facts.map((fact, i) => (
                                <li key={i} className={cn("flex items-start gap-3 text-sm", isDark ? "text-amber-100" : "text-amber-900")}>
                                    <CheckCircle2 className={cn("h-4 w-4 mt-0.5 flex-shrink-0", isDark ? "text-amber-400" : "text-amber-600")} />
                                    {fact}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* ── NEW FORMAT: Collapsible Sections ──────────────────────────── */}
                {hasNewFormat && content.sections.map((section, si) => (
                    <CollapsibleSection
                        key={si}
                        title={section.title}
                        isDark={isDark}
                        defaultOpen={si < 3}
                    >
                        <div className="space-y-1 pt-2">
                            {(section.content || []).map((block, bi) => (
                                <ContentBlock key={bi} block={block} isDark={isDark} />
                            ))}
                        </div>
                    </CollapsibleSection>
                ))}

                {/* ── DRUGS TABLE (top-level) ────────────────────────────────────── */}
                {content.drugs_table && content.drugs_table.rows?.length > 0 && (
                    <CollapsibleSection
                        title={content.drugs_table.title || 'Pharmacological Management'}
                        isDark={isDark}
                        defaultOpen={true}
                    >
                        <ContentTable
                            headers={content.drugs_table.headers}
                            rows={content.drugs_table.rows}
                            isDark={isDark}
                        />
                        {content.drugs_table.mnemonic && (
                            <MnemonicPill mnemonic={content.drugs_table.mnemonic} isDark={isDark} />
                        )}
                    </CollapsibleSection>
                )}

                {/* ── OLD FORMAT FALLBACK: individual sections ──────────────────── */}
                {!hasNewFormat && (
                    <>
                        {content.key_concepts && content.key_concepts.length > 0 && (
                            <CollapsibleSection title="Key Concepts" isDark={isDark}>
                                <div className="space-y-3 pt-2">
                                    {content.key_concepts.map((concept, idx) => (
                                        <div key={idx} className={cn("p-3 rounded-lg", isDark ? "bg-slate-900/50" : "bg-slate-50")}>
                                            <h4 className={cn("font-semibold text-sm mb-1", isDark ? "text-white" : "text-slate-900")}>
                                                {concept.term}
                                            </h4>
                                            <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                                                {concept.definition}
                                            </p>
                                            {concept.clinical_relevance && (
                                                <p className={cn("text-xs mt-1 italic", isDark ? "text-indigo-300" : "text-indigo-600")}>
                                                    {concept.clinical_relevance}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </CollapsibleSection>
                        )}
                        {content.etiology_risk_factors && (
                            <CollapsibleSection title="Etiology & Risk Factors" isDark={isDark}>
                                <p className={cn("text-sm leading-relaxed whitespace-pre-line pt-2", isDark ? "text-slate-300" : "text-slate-700")}>
                                    {content.etiology_risk_factors}
                                </p>
                            </CollapsibleSection>
                        )}
                        {content.pathophysiology && (
                            <CollapsibleSection title="Pathophysiology" isDark={isDark}>
                                <p className={cn("text-sm leading-relaxed whitespace-pre-line pt-2", isDark ? "text-slate-300" : "text-slate-700")}>
                                    {content.pathophysiology}
                                </p>
                            </CollapsibleSection>
                        )}
                        {content.clinical_presentation && (
                            <CollapsibleSection title="Clinical Presentation" isDark={isDark}>
                                <p className={cn("text-sm leading-relaxed whitespace-pre-line pt-2", isDark ? "text-slate-300" : "text-slate-700")}>
                                    {content.clinical_presentation}
                                </p>
                            </CollapsibleSection>
                        )}
                        {content.clinical_pearls && content.clinical_pearls.length > 0 && (
                            <CollapsibleSection title="Clinical Pearls" isDark={isDark}>
                                <ul className="space-y-2 pt-2">
                                    {content.clinical_pearls.map((pearl, idx) => (
                                        <li key={idx} className={cn("flex items-start gap-2 text-sm", isDark ? "text-slate-200" : "text-slate-700")}>
                                            <span className="text-emerald-500 font-bold mt-0.5">•</span>
                                            {pearl}
                                        </li>
                                    ))}
                                </ul>
                            </CollapsibleSection>
                        )}
                        {content.diagnosis && (
                            <CollapsibleSection title="Diagnosis" isDark={isDark}>
                                {typeof content.diagnosis === 'object' ? (
                                    <div className="space-y-3 pt-2">
                                        {content.diagnosis.approach && (
                                            <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-700")}>
                                                {content.diagnosis.approach}
                                            </p>
                                        )}
                                        {content.diagnosis.key_findings?.length > 0 && (
                                            <ul className="space-y-1">
                                                {content.diagnosis.key_findings.map((f, i) => (
                                                    <li key={i} className={cn("text-sm flex items-start gap-2", isDark ? "text-slate-300" : "text-slate-600")}>
                                                        <CheckCircle2 className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />{f}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ) : (
                                    <p className={cn("text-sm pt-2", isDark ? "text-slate-300" : "text-slate-700")}>{content.diagnosis}</p>
                                )}
                            </CollapsibleSection>
                        )}
                        {content.treatment && (
                            <CollapsibleSection title="Treatment" isDark={isDark}>
                                {typeof content.treatment === 'object' ? (
                                    <div className="space-y-3 pt-2">
                                        {content.treatment.overview && (
                                            <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>{content.treatment.overview}</p>
                                        )}
                                        {content.treatment.first_line && (
                                            <div className={cn("p-3 rounded-lg", isDark ? "bg-green-900/20" : "bg-green-50")}>
                                                <p className={cn("font-semibold text-xs mb-1", isDark ? "text-green-300" : "text-green-800")}>First-Line</p>
                                                <p className={cn("text-sm", isDark ? "text-green-200" : "text-green-700")}>{content.treatment.first_line}</p>
                                            </div>
                                        )}
                                        {content.treatment.alternatives && (
                                            <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>{content.treatment.alternatives}</p>
                                        )}
                                    </div>
                                ) : (
                                    <p className={cn("text-sm pt-2", isDark ? "text-slate-300" : "text-slate-700")}>{content.treatment}</p>
                                )}
                            </CollapsibleSection>
                        )}
                        {content.complications && content.complications.length > 0 && (
                            <CollapsibleSection title="Complications" isDark={isDark}>
                                <ul className="space-y-1.5 pt-2">
                                    {content.complications.map((c, i) => (
                                        <li key={i} className={cn("flex items-start gap-2 text-sm", isDark ? "text-slate-200" : "text-slate-700")}>
                                            <span className="text-red-500 mt-0.5">•</span>{c}
                                        </li>
                                    ))}
                                </ul>
                            </CollapsibleSection>
                        )}
                        {content.board_tips && (
                            <div className={cn(
                                "rounded-xl border-2 p-5",
                                isDark ? "bg-indigo-900/20 border-indigo-700" : "bg-indigo-50 border-indigo-200"
                            )}>
                                <div className="flex items-center gap-2 mb-3">
                                    <GraduationCap className={cn("h-5 w-5", isDark ? "text-indigo-400" : "text-indigo-600")} />
                                    <h3 className={cn("font-bold text-sm", isDark ? "text-indigo-300" : "text-indigo-800")} style={{ fontFamily: 'Manrope' }}>
                                        Board Exam Tips
                                    </h3>
                                </div>
                                <p className={cn("text-sm", isDark ? "text-indigo-100" : "text-indigo-900")}>{content.board_tips}</p>
                            </div>
                        )}
                    </>
                )}

                {/* ── Related Topics ────────────────────────────────────────────── */}
                {content.related_topics && content.related_topics.length > 0 && (
                    <div className={cn(
                        "rounded-xl border p-5",
                        isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"
                    )}>
                        <h3 className={cn(
                            "font-semibold text-sm mb-3",
                            isDark ? "text-white" : "text-slate-900"
                        )} style={{ fontFamily: 'Manrope' }}>
                            Related Topics
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {content.related_topics.map((relTopic, idx) => (
                                <Badge
                                    key={idx}
                                    variant="secondary"
                                    className={cn(
                                        "cursor-pointer hover:bg-indigo-100 hover:text-indigo-700 transition-colors",
                                        isDark && "bg-slate-700 text-slate-200 hover:bg-indigo-900 hover:text-indigo-300"
                                    )}
                                    onClick={() => navigate(`/library/${category}/${encodeURIComponent(decodedSystemOrSubject)}/${encodeURIComponent(relTopic)}`)}
                                >
                                    {relTopic}
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Related Practice Questions ────────────────────────────────── */}
                {(loadingQuestions || relatedQuestions.length > 0) && (
                    <div className={cn(
                        "rounded-xl border p-5",
                        isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"
                    )}>
                        <div className="flex items-center gap-2 mb-4">
                            <BookOpen className="h-5 w-5 text-indigo-500" />
                            <h3 className={cn(
                                "font-semibold text-sm",
                                isDark ? "text-white" : "text-slate-900"
                            )} style={{ fontFamily: 'Manrope' }}>
                                Practice Questions on This Topic
                            </h3>
                        </div>
                        {loadingQuestions ? (
                            <div className="flex items-center gap-2 text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading related questions...
                            </div>
                        ) : relatedQuestions.length > 0 ? (
                            <div className="space-y-3">
                                {relatedQuestions.slice(0, 5).map((q, idx) => (
                                    <div key={idx} className={cn(
                                        "p-3 rounded-lg border",
                                        isDark ? "bg-slate-900/50 border-slate-700" : "bg-slate-50 border-slate-200"
                                    )}>
                                        <p className={cn("text-sm line-clamp-2", isDark ? "text-slate-200" : "text-slate-700")}>
                                            {q.question_stem}
                                        </p>
                                        <div className="flex gap-2 mt-2">
                                            {q.subject && (
                                                <Badge variant="outline" className={cn("text-xs", isDark && "border-slate-600 text-slate-300")}>
                                                    {q.subject}
                                                </Badge>
                                            )}
                                            {q.difficulty && (
                                                <Badge variant="outline" className={cn("text-xs", isDark && "border-slate-600 text-slate-300")}>
                                                    {q.difficulty}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    variant="outline"
                                    className={cn("w-full mt-2", isDark && "border-slate-600 text-slate-200")}
                                    onClick={() => navigate('/practice')}
                                >
                                    Practice More Questions
                                    <ChevronRight className="ml-2 h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                                No related practice questions found yet.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
